import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { apiCompleteScreening } from '@/api/services/candidate_screening.service';
import { Candidate } from '@/types/Candidate';
import { Job } from '@/types/Job';
import { Screening } from '@/types/Screening';
import { fetchAPI } from '@/api/fetch';

export enum InterviewAgentStep {
  Start,
  DeviceTest,
  Practice,
  StartInterview,
  Screening,
  Completed
}

export enum InterviewAgentStatus {
  Loading,
  Started,
  Finished,
  Error,
  Expired,
  AlreadyCompleted
}

export type InterviewQuestion = {
  questionId: string;
  question: string;
  retakeCount: number;
  completed: boolean;
  file: File | null;
  videoBlob?: Blob | null;
};

interface InterviewAgentStore {
  step: InterviewAgentStep;
  status: InterviewAgentStatus;
  accessToken: string | null;
  screening: Screening | null;
  candidate: Candidate | null;
  job: Job | null;
  recordedStream: MediaStream | null;
  practiceQuestions: InterviewQuestion[];
  questions: InterviewQuestion[];
  currentQuestionId: string | null;
  counter: number;
  isCounterRunning: boolean;
  counterId: NodeJS.Timeout | null;

  setAccessToken: (accessToken: string) => void;
  setScreening: (screening: Screening) => void;
  setCandidate: (candidate: Candidate) => void;
  setJob: (job: Job) => void;
  setPracticeQuestions: (questions: InterviewQuestion[]) => void;
  setStep: (step: InterviewAgentStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  setStatus: (status: InterviewAgentStatus) => void;
  setRecordedStream: (recordedStream: MediaStream) => void;
  setQuestions: (questions: InterviewQuestion[]) => void;
  setCurrentQuestionId: (questionId: string) => void;
  submitQuestion: (questionId: string, videoBlob: Blob, file: File, retakeCount: number) => void;
  updateQuestionState: (questionId: string, videoBlob: Blob | null, retakeCount: number) => void;
  startCounterWithNumber: (counter: number) => void;
  startCounter: () => void;
  stopCounter: () => void;
  resetCounter: () => void;
  resumeCounter: () => void;
  reset: () => void;
}

export const useInterviewAgent = create<InterviewAgentStore>()(
  persist(
    (set) => ({
      step: InterviewAgentStep.Start,
      status: InterviewAgentStatus.Loading,
      accessToken: null,
      screening: null,
      candidate: null,
      job: null,
      recordedStream: null,
      questions: [],
      practiceQuestions: [],
      currentQuestionId: null,
      counter: 0,
      isCounterRunning: false,
      counterId: null,
      setAccessToken: (accessToken: string) => set({ accessToken }),
      setScreening: (screening: Screening) => set({ screening }),
      setCandidate: (candidate: Candidate) => set({ candidate }),
      setJob: (job: Job) => set({ job }),
      setPracticeQuestions: (questions: InterviewQuestion[]) =>
        set({ practiceQuestions: questions }),
      setStep: (step: InterviewAgentStep) => set({ step }),
      nextStep: () =>
        set((state) => ({
          step:
            state.step === InterviewAgentStep.Completed
              ? InterviewAgentStep.Completed
              : ((state.step + 1) as InterviewAgentStep)
        })),
      prevStep: () =>
        set((state) => ({
          step:
            state.step === InterviewAgentStep.Start
              ? InterviewAgentStep.Start
              : ((state.step - 1) as InterviewAgentStep)
        })),
      setStatus: (status: InterviewAgentStatus) => set({ status }),
      setRecordedStream: (recordedStream: MediaStream) => set({ recordedStream }),
      setQuestions: (questions: InterviewQuestion[]) => set({ questions }),
      setCurrentQuestionId: (questionId: string) => set({ currentQuestionId: questionId }),
      submitQuestion: (questionId: string, videoBlob: Blob, file: File, retakeCount: number) =>
        set((state) => {
          const questions = state.questions.map((question) => {
            if (question.questionId === questionId) {
              return { ...question, completed: true, videoBlob, file, retakeCount };
            }
            return question;
          });
          return { questions };
        }),
      updateQuestionState: (questionId: string, videoBlob: Blob | null, retakeCount: number) =>
        set((state) => {
          const questions = state.questions.map((question) => {
            if (question.questionId === questionId) {
              return { ...question, videoBlob, retakeCount };
            }
            return question;
          });
          return { questions };
        }),
      startCounterWithNumber: (counter: number) =>
        set((state) => {
          if (state.isCounterRunning) return { counter }; // just set counter if already running
          const counterId = setInterval(() => {
            set((currentState) => {
              const newCounter = currentState.counter - 1;
              if (newCounter <= 0) {
                if (currentState.counterId) {
                  clearInterval(currentState.counterId);
                }
                // Call API to complete screening when counter reaches zero
                if (currentState.accessToken && currentState.screening) {
                  fetchAPI<void>({
                    request: () => apiCompleteScreening(currentState.accessToken!)
                  }).catch((error) => {
                    console.error('Failed to complete screening:', error);
                    // Set error status if API call fails
                    set({ status: InterviewAgentStatus.Error });
                  });
                }
                return {
                  counter: 0,
                  isCounterRunning: false,
                  counterId: null,
                  step: InterviewAgentStep.Completed
                };
              }
              return { counter: newCounter };
            });
          }, 1000);
          return {
            counter,
            isCounterRunning: true,
            counterId
          };
        }),
      startCounter: () =>
        set((state) => {
          if (state.isCounterRunning || !state.screening) return state;

          const sessionTimeLimit = state.screening.session_time_limit || 0;
          const initialTime = sessionTimeLimit * 60; // Convert minutes to seconds

          const counterId = setInterval(() => {
            set((currentState) => {
              const newCounter = currentState.counter - 1;
              if (newCounter <= 0) {
                if (currentState.counterId) {
                  clearInterval(currentState.counterId);
                }
                // Call API to complete screening when counter reaches zero
                if (currentState.accessToken && currentState.screening) {
                  fetchAPI<void>({
                    request: () => apiCompleteScreening(currentState.accessToken!)
                  }).catch((error) => {
                    console.error('Failed to complete screening:', error);
                    // Set error status if API call fails
                    set({ status: InterviewAgentStatus.Error });
                  });
                }
                return {
                  counter: 0,
                  isCounterRunning: false,
                  counterId: null,
                  step: InterviewAgentStep.Completed
                };
              }
              return { counter: newCounter };
            });
          }, 1000);

          return {
            counter: initialTime,
            isCounterRunning: true,
            counterId
          };
        }),
      stopCounter: () =>
        set((state) => {
          if (state.counterId) {
            clearInterval(state.counterId);
          }
          return {
            isCounterRunning: false,
            counterId: null
          };
        }),
      resetCounter: () =>
        set((state) => {
          if (state.counterId) {
            clearInterval(state.counterId);
          }
          const sessionTimeLimit = state.screening?.session_time_limit || 0;
          const initialTime = sessionTimeLimit * 60;
          return {
            counter: initialTime,
            isCounterRunning: false,
            counterId: null
          };
        }),
      resumeCounter: () =>
        set((state) => {
          if (!state.isCounterRunning || state.counter <= 0) return state;

          if (state.counterId) {
            clearInterval(state.counterId);
          }

          const counterId = setInterval(() => {
            set((currentState) => {
              const newCounter = currentState.counter - 1;
              if (newCounter <= 0) {
                if (currentState.counterId) {
                  clearInterval(currentState.counterId);
                }
                // Call API to complete screening when counter reaches zero
                if (currentState.accessToken && currentState.screening) {
                  fetchAPI<void>({
                    request: () => apiCompleteScreening(currentState.accessToken!)
                  }).catch((error) => {
                    console.error('Failed to complete screening:', error);
                    // Set error status if API call fails
                    set({ status: InterviewAgentStatus.Error });
                  });
                }
                return {
                  counter: 0,
                  isCounterRunning: false,
                  counterId: null,
                  step: InterviewAgentStep.Completed
                };
              }
              return { counter: newCounter };
            });
          }, 1000);

          return {
            counterId
          };
        }),
      reset: () =>
        set({
          accessToken: null,
          screening: null,
          candidate: null,
          job: null,
          recordedStream: null,
          step: InterviewAgentStep.Start,
          status: InterviewAgentStatus.Loading,
          practiceQuestions: [],
          questions: [],
          currentQuestionId: null,
          counter: 0,
          isCounterRunning: false,
          counterId: null
        })
    }),
    {
      name: 'interview-agent-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist these fields, exclude recordedStream and File objects
      partialize: (state) => ({
        step: state.step,
        status: state.status,
        accessToken: state.accessToken,
        screening: state.screening,
        candidate: state.candidate,
        job: state.job,
        questions: state.questions.map((q) => ({
          ...q
        })),
        practiceQuestions: state.practiceQuestions.map((q) => ({
          ...q
        })),
        currentQuestionId: state.currentQuestionId,
        counter: state.counter,
        isCounterRunning: state.isCounterRunning
      })
    }
  )
);
